import React, { useState, useEffect, useCallback } from 'react';
import { dispatchAPI, uploadAPI, salesPipelineAPI, orderAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import RecordDetailsModal, { ActionButton } from '../../components/common/RecordDetailsModal';
import { formatPaymentBasis } from '../../utils/salesOrderRelease';
import OrderWorkflowBanner from '../../components/common/OrderWorkflowBanner';
import {
  DispatchQcModal, QcStatusBadge, QcActionButton,
  canRunDispatchQc, isQcCleared, QC_STATUS_STYLE
} from '../../components/common/DispatchQualityCheck';
import {
  HiOutlineTruck, HiOutlinePlus, HiOutlineSearch,
  HiOutlineX, HiOutlinePencil, HiOutlineTrash,
  HiOutlineRefresh, HiOutlineSave, HiOutlineUpload,
  HiOutlineDocumentText, HiOutlinePhotograph,
  HiOutlineVideoCamera, HiOutlineReceiptTax, HiOutlineChevronRight,
  HiOutlineMail, HiOutlineEye
} from 'react-icons/hi';

const formatExternalUrl = (url) => {
  if (!url) return '#';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('/api/uploads/')) {
    const cleanPath = trimmed.startsWith('/api/') ? trimmed : `/api${trimmed}`;
    const apiURL = process.env.REACT_APP_API_URL;
    if (apiURL) {
      const base = apiURL.endsWith('/api') ? apiURL.slice(0, -4) : (apiURL.endsWith('/api/') ? apiURL.slice(0, -5) : apiURL);
      return `${base}${cleanPath}`;
    }
    return window.location.port
      ? `${window.location.protocol}//${window.location.hostname}:9898${cleanPath}`
      : `${window.location.origin}${cleanPath}`;
  }
  return `https://${trimmed}`;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  return /\.(mp4|mov|webm|mkv|avi|3gp|m4v)(\?.*)?$/i.test(url);
};

const isPdfUrl = (url) => {
  if (!url) return false;
  return /\.(pdf)(\?.*)?$/i.test(url);
};

const STATUS_CONFIG = {
  assemble: { label: '1. Assembly', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: '🔧', stepNum: 1 },
  packing: { label: '2. Packing', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', icon: '📦', stepNum: 2 },
  dispatched: { label: '3. Dispatched', color: '#22c55e', bg: 'rgba(34,197,94,0.15)', icon: '🚚', stepNum: 3 },
  hold: { label: 'On Hold', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', icon: '⏸️', stepNum: 0 },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

// The Sales Order number the job came from, or a short job id for manual jobs
const getDispatchNumber = (d) => d.salesOrderRef || `JOB-${String(d._id || '').slice(-6).toUpperCase()}`;

const BLANK_FORM = {
  customerName: '', customerContact: '', customerEmail: '', notifyCustomerEmail: true,
  customerAddress: '', salesOrderRef: '',
  items: [{ modelNumber: '', description: '', quantity: 1, unit: 'meters' }],
  status: 'assemble',
  // Assembly stage
  assemblyMediaUrl: '', assemblyNotes: '',
  // Packing stage
  packingDetails: '', packingMediaUrl: '', packingNotes: '', totalBoxes: '', totalWeight: '',
  // Dispatch & Logistics stage
  dispatchMediaUrl: '', courier: '', trackingNumber: '', lrNumber: '', lrCopyUrl: '',
  // Billing & Invoice stage
  billingInvoiceUrl: '', invoiceNumber: '', invoiceAmount: '', invoiceDate: '',
  // POD & Hold
  podUrl: '', holdReason: '', remarks: ''
};

// Reusable media uploader with direct drag-and-drop & file selection
function MediaUploader({ label, fileUrl, onUploadSuccess, accept = "image/*,video/*,application/pdf", required = false, hint = "Upload Photo, Video or PDF" }) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await uploadAPI.uploadFile(formData);
      if (res.data?.fileUrl) {
        onUploadSuccess(res.data.fileUrl);
        toast.success(`${label} uploaded successfully!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const isVid = isVideoUrl(fileUrl);
  const isPdf = isPdfUrl(fileUrl);

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', border: '1px dashed var(--border)', marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label className="form-label" style={{ fontSize: '0.8rem', margin: 0, fontWeight: 600 }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
        {fileUrl && (
          <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            ✓ File Attached
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px', background: uploading ? 'rgba(255,255,255,0.05)' : 'var(--primary)',
          color: '#fff', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600,
          cursor: uploading ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
        }}>
          {uploading ? <div className="spinner-sm" /> : <HiOutlineUpload />}
          {uploading ? 'Uploading...' : fileUrl ? 'Change File' : 'Choose File / Photo / Video'}
          <input type="file" accept={accept} onChange={handleFileChange} disabled={uploading} style={{ display: 'none' }} />
        </label>

        <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
          {hint} (Max 50MB)
        </span>
      </div>

      {fileUrl && (
        <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isVid ? (
            <video src={formatExternalUrl(fileUrl)} style={{ height: '50px', borderRadius: '4px', maxWidth: '100px' }} controls={false} />
          ) : isPdf ? (
            <div style={{ fontSize: '1.8rem', color: '#ef4444' }}><HiOutlineDocumentText /></div>
          ) : (
            <img src={formatExternalUrl(fileUrl)} alt="Preview" style={{ height: '50px', width: '50px', objectFit: 'cover', borderRadius: '4px' }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-primary)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {fileUrl.split('/').pop()}
            </div>
            <a href={formatExternalUrl(fileUrl)} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>
              🔗 Open & View File
            </a>
          </div>
          <button type="button" onClick={() => onUploadSuccess('')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem' }} title="Remove file">
            <HiOutlineX />
          </button>
        </div>
      )}
    </div>
  );
}

export default function DispatchTab({ readOnly = false }) {
  const { user } = useAuth();
  const canApproveGate = ['super_admin', 'admin', 'gate_guard'].includes(user?.role);
  const canEditDispatch = !readOnly && user?.role !== 'gate_guard';

  const [dispatches, setDispatches] = useState([]);
  const [stats, setStats] = useState({ assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Pre-Dispatch Quality Check modal (meters only)
  const [showQcModal, setShowQcModal] = useState(false);
  const [qcOrder, setQcOrder] = useState(null);
  const [viewDispatch, setViewDispatch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Media preview modal state
  const [previewMedia, setPreviewMedia] = useState(null); // { url, title, type }

  // Quick Stage Transition Modal State
  const [stageModal, setStageModal] = useState({
    open: false,
    dispatch: null,
    targetStage: '', // 'packing', 'dispatched', 'assemble', 'hold'
    mediaUrl: '',
    notes: '',
    boxes: '',
    weight: '',
    invoiceUrl: '',
    invoiceNo: '',
    invoiceAmt: '',
    lrNo: '',
    lrCopy: '',
    courier: '',
    tracking: '',
    customerEmail: '',
    notifyCustomerEmail: true
  });

  const [salesOrders, setSalesOrders] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [dRes, sRes, pipelineRes, orderRes] = await Promise.all([
        dispatchAPI.getAll(filterStatus ? { status: filterStatus } : {}).catch(err => {
          console.error('[Dispatch getAll error]:', err);
          return { data: { data: [] } };
        }),
        dispatchAPI.getStats().catch(err => {
          console.error('[Dispatch getStats error]:', err);
          return { data: { data: { assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 } } };
        }),
        salesPipelineAPI.getAll().catch(() => ({ data: { data: [] } })),
        orderAPI.getOrders().catch(() => ({ data: { data: [] } }))
      ]);
      setDispatches(Array.isArray(dRes.data?.data) ? dRes.data.data : (Array.isArray(dRes.data) ? dRes.data : []));
      setStats(sRes.data?.data || { assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 });

      const pipelineList = pipelineRes.data?.data || [];
      const ordersList = Array.isArray(orderRes.data?.data) ? orderRes.data.data : (Array.isArray(orderRes.data) ? orderRes.data : []);

      const combined = [];

      pipelineList.forEach(p => {
        // Sales Orders reach the store only after Admin approval
        if (p.stage === 'sales_order' && p.approvalStatus !== 'approved') return;
        const refNo = p.salesOrderNumber || p.quotationNumber || p.piNumber || (p._id ? `SO-${p._id.slice(-6).toUpperCase()}` : '');
        if (refNo && !combined.some(c => c.salesOrderRef === refNo)) {
          combined.push({
            source: 'pipeline',
            id: p._id,
            salesOrderRef: refNo,
            customerName: p.customerName || '',
            customerContact: p.customerContact || '',
            customerEmail: p.customerEmail || '',
            customerAddress: p.customerAddress || '',
            stage: p.stage,
            items: (p.items || []).map(it => ({
              modelNumber: it.modelNumber || it.dragChainConfig?.modelNumber || '',
              description: it.description || (it.dragChainConfig ? `${it.dragChainConfig.width || ''} R${it.dragChainConfig.radius || ''} ${it.dragChainConfig.chainType || ''}` : ''),
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'meters'
            }))
          });
        }
      });

      ordersList.forEach(o => {
        const refNo = o.orderNumber || (o._id ? `ORD-${o._id.slice(-6).toUpperCase()}` : '');
        if (refNo && !combined.some(c => c.salesOrderRef === refNo)) {
          combined.push({
            source: 'orders',
            id: o._id,
            salesOrderRef: refNo,
            customerName: o.clientName || '',
            customerContact: '',
            customerEmail: '',
            customerAddress: '',
            stage: o.status,
            items: [{
              modelNumber: o.modelNumber || '',
              description: `Order ${o.orderNumber || ''}`,
              quantity: Number(o.orderQuantity) || 1,
              unit: 'meters'
            }]
          });
        }
      });

      setSalesOrders(combined);
    } catch { toast.error('Failed to load dispatch data'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  const handleSelectSalesOrder = (refNumber) => {
    if (!refNumber) {
      setForm(p => ({ ...p, salesOrderRef: '' }));
      return;
    }
    if (refNumber === '__custom__') {
      setForm(p => ({ ...p, salesOrderRef: '' }));
      return;
    }

    const selectedSO = salesOrders.find(so => so.salesOrderRef === refNumber);
    if (selectedSO) {
      setForm(p => ({
        ...p,
        salesOrderRef: selectedSO.salesOrderRef,
        customerName: selectedSO.customerName || p.customerName,
        customerContact: selectedSO.customerContact || p.customerContact,
        customerEmail: selectedSO.customerEmail || p.customerEmail,
        customerAddress: selectedSO.customerAddress || p.customerAddress,
        items: selectedSO.items && selectedSO.items.length > 0 
          ? selectedSO.items.map(it => ({
              modelNumber: it.modelNumber || '',
              description: it.description || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'meters'
            }))
          : p.items
      }));
      toast.success(`Loaded items & details from Sales Order: ${selectedSO.salesOrderRef}!`);
    } else {
      setForm(p => ({ ...p, salesOrderRef: refNumber }));
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGatePass = async (id, gateStatus) => {
    let gateRemarks = '';
    if (gateStatus === 'declined') {
      gateRemarks = window.prompt('Enter reason for declining/holding vehicle at gate:') || 'Failed Gate Inspection';
      if (!gateRemarks) return;
    }
    try {
      await dispatchAPI.update(id, { gateStatus, gateRemarks });
      toast.success(gateStatus === 'approved' ? '✅ Gate Outbound Pass Approved!' : '❌ Gate Pass Declined / Stopped');
      fetchData();
    } catch {
      toast.error('Failed to update Gate Approval status');
    }
  };

  const openCreate = () => { setForm(BLANK_FORM); setEditingId(null); setModalStep(1); setShowModal(true); };
  const openEdit = (d) => {
    setForm({
      customerName: d.customerName || '', customerContact: d.customerContact || '',
      customerEmail: d.customerEmail || '', notifyCustomerEmail: d.notifyCustomerEmail !== false,
      customerAddress: d.customerAddress || '', salesOrderRef: d.salesOrderRef || '',
      items: d.items?.length ? d.items : BLANK_FORM.items,
      status: d.status || 'assemble',
      assemblyMediaUrl: d.assemblyMediaUrl || '', assemblyNotes: d.assemblyNotes || '',
      packingDetails: d.packingDetails || '', packingMediaUrl: d.packingMediaUrl || '',
      packingNotes: d.packingNotes || '', totalBoxes: d.totalBoxes || '', totalWeight: d.totalWeight || '',
      dispatchMediaUrl: d.dispatchMediaUrl || '', courier: d.courier || '',
      trackingNumber: d.trackingNumber || '', lrNumber: d.lrNumber || '',
      lrCopyUrl: d.lrCopyUrl || '', billingInvoiceUrl: d.billingInvoiceUrl || '',
      invoiceNumber: d.invoiceNumber || '', invoiceAmount: d.invoiceAmount || '',
      invoiceDate: d.invoiceDate ? d.invoiceDate.split('T')[0] : '',
      podUrl: d.podUrl || '', holdReason: d.holdReason || '', remarks: d.remarks || ''
    });
    setEditingId(d._id);
    setModalStep(1);
    setShowModal(true);
  };

  const handleSaveDraft = async (e) => {
    if (e) e.preventDefault();
    if (!form.customerName?.trim()) {
      toast.error('Please enter Customer Name to save draft');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await dispatchAPI.update(editingId, form);
        toast.success('💾 Draft saved successfully!');
      } else {
        const res = await dispatchAPI.create(form);
        const createdId = res.data?.data?._id || res.data?._id;
        if (createdId) setEditingId(createdId);
        toast.success('💾 Saved as draft!');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await dispatchAPI.update(editingId, form);
        toast.success('Dispatch order updated successfully');
      } else {
        await dispatchAPI.create(form);
        toast.success('Dispatch order created successfully');
      }
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dispatch order?')) return;
    try { await dispatchAPI.delete(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  // Trigger manual on-demand email notification
  const handleSendManualEmail = async (d) => {
    const defaultEmail = d.customerEmail || '';
    const email = window.prompt('Enter customer email to send dispatch notification:', defaultEmail);
    if (!email) return;
    try {
      toast.loading('Sending email notification...', { id: 'sendEmail' });
      await dispatchAPI.sendEmail(d._id, { email });
      toast.success(`📧 Email notification sent to ${email}`, { id: 'sendEmail' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send email', { id: 'sendEmail' });
    }
  };

  // Open Quick Stage Transition Modal
  const openStageTransition = (d, targetStage) => {
    setStageModal({
      open: true,
      dispatch: d,
      targetStage,
      mediaUrl: targetStage === 'packing' ? (d.packingMediaUrl || '') : targetStage === 'dispatched' ? (d.dispatchMediaUrl || '') : (d.assemblyMediaUrl || ''),
      notes: targetStage === 'packing' ? (d.packingNotes || '') : (d.assemblyNotes || ''),
      boxes: d.totalBoxes || '',
      weight: d.totalWeight || '',
      invoiceUrl: d.billingInvoiceUrl || '',
      invoiceNo: d.invoiceNumber || '',
      invoiceAmt: d.invoiceAmount || '',
      lrNo: d.lrNumber || '',
      lrCopy: d.lrCopyUrl || '',
      courier: d.courier || '',
      tracking: d.trackingNumber || '',
      customerEmail: d.customerEmail || '',
      notifyCustomerEmail: true
    });
  };

  // Submit Quick Stage Transition
  const handleStageTransitionSubmit = async (e) => {
    e.preventDefault();
    if (!stageModal.dispatch) return;
    setSubmitting(true);
    try {
      const updates = {
        status: stageModal.targetStage,
        customerEmail: stageModal.customerEmail,
        notifyCustomerEmail: stageModal.notifyCustomerEmail
      };
      if (stageModal.targetStage === 'packing') {
        updates.assemblyMediaUrl = stageModal.mediaUrl;
        updates.assemblyNotes = stageModal.notes;
        updates.totalBoxes = stageModal.boxes;
        updates.totalWeight = stageModal.weight;
      } else if (stageModal.targetStage === 'dispatched') {
        updates.packingMediaUrl = stageModal.mediaUrl;
        updates.dispatchMediaUrl = stageModal.mediaUrl;
        updates.billingInvoiceUrl = stageModal.invoiceUrl;
        updates.invoiceNumber = stageModal.invoiceNo;
        updates.invoiceAmount = stageModal.invoiceAmt;
        updates.lrNumber = stageModal.lrNo;
        updates.lrCopyUrl = stageModal.lrCopy;
        updates.courier = stageModal.courier;
        updates.trackingNumber = stageModal.tracking;
      }
      await dispatchAPI.update(stageModal.dispatch._id, updates);
      toast.success(`Order successfully moved to ${STATUS_CONFIG[stageModal.targetStage]?.label || stageModal.targetStage}!`);
      if (stageModal.customerEmail && stageModal.notifyCustomerEmail) {
        toast.success(`📧 Notification email sent to ${stageModal.customerEmail}`);
      }
      setStageModal({ open: false, dispatch: null, targetStage: '' });
      fetchData();
    } catch (err) {
      // Mandatory pre-dispatch quality check blocked this transition
      if (err.response?.data?.qcBlocked) {
        const blocked = stageModal.dispatch;
        toast.error(err.response.data.message, { duration: 6000 });
        setStageModal({ open: false, dispatch: null, targetStage: '' });
        if (canRunQc && blocked) handleOpenQcModal(blocked);
        return;
      }
      toast.error(err.response?.data?.message || 'Failed to update stage');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Pre-Dispatch Quality Check (meters only) =====
  const canRunQc = canRunDispatchQc(user);

  const handleOpenQcModal = (order) => {
    setQcOrder(order);
    setShowQcModal(true);
  };

  const handleCloseQcModal = () => {
    setShowQcModal(false);
    setQcOrder(null);
  };

  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: val };
    setForm(p => ({ ...p, items }));
  };
  const addItem = () => setForm(p => ({ ...p, items: [...p.items, { modelNumber: '', description: '', quantity: 1, unit: 'meters' }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const filtered = dispatches.filter(d =>
    d.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    d.customerEmail?.toLowerCase().includes(search.toLowerCase()) ||
    d.salesOrderRef?.toLowerCase().includes(search.toLowerCase()) ||
    d.lrNumber?.toLowerCase().includes(search.toLowerCase()) ||
    d.invoiceNumber?.toLowerCase().includes(search.toLowerCase())
  );

  const statCards = [
    { key: 'assemble', label: '1. Assembly', ...STATUS_CONFIG.assemble },
    { key: 'packing', label: '2. Packing', ...STATUS_CONFIG.packing },
    { key: 'dispatched', label: '3. Dispatched', ...STATUS_CONFIG.dispatched },
    { key: 'hold', label: 'On Hold', ...STATUS_CONFIG.hold },
  ];

  return (
    <div>
      {/* Top Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🔧 Assembly ➔ 📦 Packing ➔ 🚚 Dispatch & Billing Workflow
          </h3>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            Attach Photo/Video proofs in all steps, upload Billing/Invoice, and notify customer via email with tracking & attachments.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchData} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <HiOutlineRefresh /> Refresh
          </button>
          {!readOnly && canEditDispatch && (
            <button onClick={openCreate} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HiOutlinePlus /> New Order for Assembly
            </button>
          )}
        </div>
      </div>

      {/* End-to-End 9-Step Lifecycle Banner */}
      <OrderWorkflowBanner activeStageKey={filterStatus || 'assemble'} />

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginBottom: '20px' }}>
        {statCards.map(s => (
          <div key={s.key} onClick={() => setFilterStatus(filterStatus === s.key ? '' : s.key)}
            style={{
              background: filterStatus === s.key ? s.bg : 'var(--bg-card)',
              border: `1px solid ${filterStatus === s.key ? s.color : 'var(--border)'}`,
              borderRadius: '12px', padding: '16px', cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>{s.icon}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{stats[s.key] || 0}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search customer, email, order ref, LR no, invoice no..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: '180px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Stages</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Dispatch Table — ID, Customer, Stage, Date, Actions. Full details open in the View modal. */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-card-hover)' }}>
                {['ID', 'Customer Name', 'Stage', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.76rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>Loading orders...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
                  <HiOutlineTruck style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto 8px' }} />
                  No orders found in this stage
                </td></tr>
              ) : filtered.map(d => {
                const sc = STATUS_CONFIG[d.status] || STATUS_CONFIG.assemble;
                const needsQc = canRunQc && !['packing', 'dispatched'].includes(d.status);
                return (
                  <tr key={d._id} onClick={() => setViewDispatch(d)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 800, color: 'var(--primary)', fontFamily: 'ui-monospace, Consolas, monospace' }}>{getDispatchNumber(d)}</div>
                      {d.items?.length > 0 && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                          {d.items.length} item{d.items.length > 1 ? 's' : ''} · {d.items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0)} {d.items[0]?.unit || 'meters'}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{d.customerName}</div>
                      {d.customerContact && <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>📞 {d.customerContact}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: sc.bg, color: sc.color, border: `1px solid ${sc.color}55`, borderRadius: '999px', padding: '3px 10px', fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {sc.icon} {sc.label}
                        </span>
                        <QcStatusBadge order={d} />
                      </div>
                      {d.statusBeforeApprovalHold && (
                        <div style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 700, marginTop: '4px' }}>⏸️ Awaiting Admin re-approval</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatDate(d.releasedAt || d.createdAt)}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                        {needsQc && !isQcCleared(d) && <QcActionButton order={d} onClick={handleOpenQcModal} />}
                        {canEditDispatch && d.status === 'assemble' && (
                          <ActionButton icon={<HiOutlineChevronRight />} label="Move to Packing" color="#2563eb" onClick={() => openStageTransition(d, 'packing')} title="Complete Assembly & Move to Packing" />
                        )}
                        {canEditDispatch && d.status === 'packing' && (
                          <ActionButton icon={<HiOutlineChevronRight />} label="Dispatch & Bill" color="#16a34a" onClick={() => openStageTransition(d, 'dispatched')} title="Attach Bill, LR & Dispatch" />
                        )}
                        <ActionButton icon={<HiOutlineEye />} label="View" color="#2563eb" onClick={() => setViewDispatch(d)} title="View full details" />
                        {canEditDispatch && (
                          <>
                            <ActionButton icon={<HiOutlinePencil />} label="Edit" color="#475569" onClick={() => openEdit(d)} />
                            <ActionButton icon={<HiOutlineTrash />} label="Delete" color="#dc2626" onClick={() => handleDelete(d._id)} />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* VIEW FULL DETAILS MODAL */}
      {viewDispatch && (() => {
        const d = viewDispatch;
        const sc = STATUS_CONFIG[d.status] || STATUS_CONFIG.assemble;
        const qc = QC_STATUS_STYLE[d.qcStatus] || QC_STATUS_STYLE.pending;
        const closeAnd = (fn) => () => { setViewDispatch(null); fn(); };
        const userName = (u) => (u && typeof u === 'object' ? u.name : '');
        const mediaLink = (url, title) => (url ? (
          <button type="button" onClick={() => setPreviewMedia({ url, title: `${title} - ${d.customerName}`, type: isVideoUrl(url) ? 'video' : 'image' })}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {isVideoUrl(url) ? <HiOutlineVideoCamera /> : <HiOutlinePhotograph />} View {isVideoUrl(url) ? 'Video' : isPdfUrl(url) ? 'PDF' : 'Photo'}
          </button>
        ) : '');
        const fileLink = (url, label) => (url ? <a href={formatExternalUrl(url)} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>📄 {label}</a> : '');
        const gateLabel = { approved: '✅ Gate Passed', declined: '❌ Stopped at Gate', pending: '⏳ Gate Pending' }[d.gateStatus || 'pending'];

        const sections = [
          {
            title: 'Order Summary', icon: '📋', rows: [
              { label: 'Order ID', value: getDispatchNumber(d) },
              { label: 'Sales Order Ref.', value: d.salesOrderRef },
              { label: 'Current Stage', value: `${sc.icon} ${sc.label}` },
              { label: 'Hold Reason', value: d.status === 'hold' || d.statusBeforeApprovalHold ? (d.holdReason || 'Awaiting Admin re-approval') : '' },
              { label: 'Released By', value: d.releasedByName, hint: 'Sales person who raised the order' },
              { label: 'Released On', value: formatDateTime(d.releasedAt) },
              { label: 'Approved By', value: d.approvedByName, hint: 'Admin who released it to the store' },
              { label: 'Approved On', value: formatDateTime(d.approvedAt) },
              { label: 'Payment Basis', value: formatPaymentBasis(d) },
              { label: 'Created On', value: formatDateTime(d.createdAt) },
              { label: 'Last Updated', value: formatDateTime(d.updatedAt) },
              { label: 'Handled By', value: userName(d.handledBy) }
            ]
          },
          {
            title: 'Customer Details', icon: '🏢', rows: [
              { label: 'Customer Name', value: d.customerName },
              { label: 'Contact Number', value: d.customerContact },
              { label: 'Email', value: d.customerEmail },
              { label: 'Email Updates', value: d.customerEmail ? (d.notifyCustomerEmail !== false ? 'On — customer is notified at each step' : 'Off') : '' },
              { label: 'GSTIN', value: d.customerGST },
              { label: 'Address', value: d.customerAddress },
              { label: 'Billing Address', value: d.billingAddress },
              { label: 'Shipping Address', value: d.shippingAddress }
            ]
          },
          {
            title: 'Items to Assemble', icon: '⛓️', wide: true, table: {
              columns: ['#', 'Model', 'Description', 'Quantity', 'Unit'],
              rows: (d.items || []).map((it, i) => [i + 1, it.modelNumber, it.description, it.quantity, it.unit])
            }
          },
          {
            title: 'Quality Check', icon: '🔍', rows: [
              { label: 'QC Status', value: `${qc.icon} ${qc.label}`, hint: 'Must pass before Packing' },
              { label: 'Inspection Type', value: d.qcCheckedAt ? (d.qcInspectionType === 'final_inspection' ? 'Final Inspection' : 'First Inspection') : '' },
              { label: 'Total Meters Inspected', value: d.qcTotalMeters ? `${d.qcTotalMeters} m` : '' },
              { label: 'Passed Meters', value: d.qcPassedMeters ? `${d.qcPassedMeters} m` : '' },
              { label: 'Rejected Meters', value: d.qcRejectedMeters ? `${d.qcRejectedMeters} m` : '' },
              { label: 'Inspector', value: d.qcCheckedByName || userName(d.qcCheckedBy) },
              { label: 'Checked On', value: formatDateTime(d.qcCheckedAt) },
              { label: 'QC Remarks', value: d.qcRemarks },
              { label: 'No. of Inspections', value: d.qcHistory?.length ? `${d.qcHistory.length}` : '' }
            ]
          },
          {
            title: 'Assembly & Packing', icon: '📦', rows: [
              { label: 'Assembled On', value: formatDateTime(d.assembledAt) },
              { label: 'Assembled By', value: userName(d.assembledBy) },
              { label: 'Assembly Proof', value: mediaLink(d.assemblyMediaUrl, 'Assembly Proof') },
              { label: 'Assembly Notes', value: d.assemblyNotes },
              { label: 'Packed On', value: formatDateTime(d.packedAt) },
              { label: 'Packed By', value: userName(d.packedBy) },
              { label: 'Total Boxes', value: d.totalBoxes || '' },
              { label: 'Total Weight', value: d.totalWeight },
              { label: 'Packing Details', value: d.packingDetails },
              { label: 'Packing Proof', value: mediaLink(d.packingMediaUrl, 'Packing Proof') },
              { label: 'Packing Notes', value: d.packingNotes }
            ]
          },
          {
            title: 'Billing & Invoice', icon: '🧾', rows: [
              { label: 'Invoice No.', value: d.invoiceNumber },
              { label: 'Invoice Amount', value: d.invoiceAmount > 0 ? `₹${Number(d.invoiceAmount).toLocaleString('en-IN')}` : '' },
              { label: 'Invoice Date', value: formatDate(d.invoiceDate) },
              { label: 'Bill File', value: fileLink(d.billingInvoiceUrl, 'View Attached Bill') }
            ]
          },
          {
            title: 'Dispatch & Logistics', icon: '🚚', rows: [
              { label: 'Dispatched On', value: formatDateTime(d.dispatchedAt) },
              { label: 'Dispatched By', value: userName(d.dispatchedBy) },
              { label: 'Loading Proof', value: mediaLink(d.dispatchMediaUrl, 'Dispatch Loading Proof') },
              { label: 'Courier / Transporter', value: d.courier },
              { label: 'Tracking (AWB) No.', value: d.trackingNumber },
              { label: 'LR No.', value: d.lrNumber, hint: 'Lorry Receipt from transporter' },
              { label: 'LR Copy', value: fileLink(d.lrCopyUrl, 'View LR Copy') },
              { label: 'Proof of Delivery', value: fileLink(d.podUrl, 'View POD') },
              { label: 'POD Received On', value: formatDateTime(d.podReceivedAt) }
            ]
          },
          {
            title: 'Gate Pass', icon: '🚧', rows: [
              { label: 'Gate Status', value: gateLabel },
              { label: 'Gate Approved By', value: userName(d.gateApprovedBy) },
              { label: 'Remarks', value: d.remarks }
            ]
          }
        ];

        return (
          <RecordDetailsModal
            title={`${getDispatchNumber(d)} · ${d.customerName}`}
            subtitle={d.approvedByName ? `Approved by ${d.approvedByName}${formatPaymentBasis(d) ? ` · ${formatPaymentBasis(d)}` : ''}` : 'Store order'}
            badges={[
              { label: `${sc.icon} ${sc.label}`, color: sc.color, bg: sc.bg },
              { label: `${qc.icon} ${qc.label}`, color: qc.color, bg: qc.bg },
              { label: gateLabel, color: d.gateStatus === 'approved' ? '#16a34a' : d.gateStatus === 'declined' ? '#dc2626' : '#d97706', bg: 'rgba(148,163,184,0.12)' }
            ]}
            sections={sections}
            onClose={() => setViewDispatch(null)}
            actions={
              <>
                {canRunQc && !['packing', 'dispatched'].includes(d.status) && (
                  <QcActionButton order={d} onClick={(o) => { setViewDispatch(null); handleOpenQcModal(o); }} />
                )}
                {canApproveGate && d.gateStatus !== 'approved' && (
                  <button className="btn btn-secondary" onClick={closeAnd(() => handleGatePass(d._id, 'approved'))} style={{ color: '#16a34a', fontWeight: 700 }}>✅ Gate Pass</button>
                )}
                {canApproveGate && d.gateStatus !== 'declined' && (
                  <button className="btn btn-secondary" onClick={closeAnd(() => handleGatePass(d._id, 'declined'))} style={{ color: '#dc2626', fontWeight: 700 }}>❌ Stop at Gate</button>
                )}
                {canEditDispatch && (
                  <>
                    <button className="btn btn-secondary" onClick={closeAnd(() => handleSendManualEmail(d))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <HiOutlineMail /> Email Customer
                    </button>
                    {d.status === 'assemble' && (
                      <button className="btn btn-primary" onClick={closeAnd(() => openStageTransition(d, 'packing'))}>📦 Move to Packing</button>
                    )}
                    {d.status === 'packing' && (
                      <button className="btn btn-primary" onClick={closeAnd(() => openStageTransition(d, 'dispatched'))} style={{ background: '#16a34a', borderColor: '#16a34a' }}>🚚 Dispatch & Bill</button>
                    )}
                    <button className="btn btn-primary" onClick={closeAnd(() => openEdit(d))} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <HiOutlinePencil /> Edit
                    </button>
                  </>
                )}
              </>
            }
          />
        );
      })()}

      {/* QUICK STAGE PROGRESSION MODAL */}
      {stageModal.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px'
        }} onClick={e => e.target === e.currentTarget && setStageModal({ open: false, dispatch: null, targetStage: '' })}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '580px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--primary)' }}>
                {stageModal.targetStage === 'packing' ? '📦 Step 2: Complete Assembly ➔ Move to Packing' : '🚚 Step 3: Complete Packing ➔ Dispatch & Attach Bill'}
              </h3>
              <button onClick={() => setStageModal({ open: false, dispatch: null, targetStage: '' })} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}>
                <HiOutlineX />
              </button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
              <form onSubmit={handleStageTransitionSubmit}>
                <div style={{ marginBottom: '14px', padding: '10px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Customer: <strong>{stageModal.dispatch?.customerName}</strong></div>
                  {stageModal.dispatch?.salesOrderRef && <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>SO Ref: <strong>{stageModal.dispatch.salesOrderRef}</strong></div>}
                </div>

              {/* Transition: Assembly to Packing */}
              {stageModal.targetStage === 'packing' && (
                <>
                  <MediaUploader
                    label="Assembly Photo / Video Proof"
                    fileUrl={stageModal.mediaUrl}
                    onUploadSuccess={url => setStageModal(p => ({ ...p, mediaUrl: url }))}
                    hint="Upload photo or video of assembled product"
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Box Count</label>
                      <input className="form-input" type="number" min={1} value={stageModal.boxes} onChange={e => setStageModal(p => ({ ...p, boxes: e.target.value }))} placeholder="e.g. 3 Boxes" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Weight</label>
                      <input className="form-input" value={stageModal.weight} onChange={e => setStageModal(p => ({ ...p, weight: e.target.value }))} placeholder="e.g. 45 KG" />
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Assembly / Packing Remarks</label>
                    <textarea className="form-input" rows={2} value={stageModal.notes} onChange={e => setStageModal(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Assembled with 100 links, packed securely..." />
                  </div>
                </>
              )}

              {/* Transition: Packing to Dispatched */}
              {stageModal.targetStage === 'dispatched' && (
                <>
                  <MediaUploader
                    label="Dispatch / Vehicle Loading Photo / Video"
                    fileUrl={stageModal.mediaUrl}
                    onUploadSuccess={url => setStageModal(p => ({ ...p, mediaUrl: url }))}
                    hint="Upload photo/video of vehicle loading or parcel"
                  />

                  <div style={{ background: 'rgba(16,185,129,0.03)', padding: '14px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.2)', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#10b981', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <HiOutlineReceiptTax /> Billing & Invoice Details
                    </div>
                    
                    <MediaUploader
                      label="Attach Tax Invoice / Bill Document (PDF / Image)"
                      fileUrl={stageModal.invoiceUrl}
                      onUploadSuccess={url => setStageModal(p => ({ ...p, invoiceUrl: url }))}
                      hint="Upload Invoice PDF or Photo"
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Invoice Number</label>
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={stageModal.invoiceNo} onChange={e => setStageModal(p => ({ ...p, invoiceNo: e.target.value }))} placeholder="e.g. INV-2026-908" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Invoice Total Amount (₹)</label>
                        <input className="form-input" type="number" style={{ fontSize: '0.8rem' }} value={stageModal.invoiceAmt} onChange={e => setStageModal(p => ({ ...p, invoiceAmt: e.target.value }))} placeholder="e.g. 54000" />
                      </div>
                    </div>
                  </div>

                  <div style={{ background: 'rgba(59,130,246,0.03)', padding: '14px', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#3b82f6', marginBottom: '8px' }}>
                      🚚 Logistics & LR Details
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Courier Name</label>
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={stageModal.courier} onChange={e => setStageModal(p => ({ ...p, courier: e.target.value }))} placeholder="e.g. DTDC / SafeExpress" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Tracking / AWB No.</label>
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={stageModal.tracking} onChange={e => setStageModal(p => ({ ...p, tracking: e.target.value }))} placeholder="AWB Number" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>LR Number</label>
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={stageModal.lrNo} onChange={e => setStageModal(p => ({ ...p, lrNo: e.target.value }))} placeholder="Lorry Receipt No." />
                      </div>
                      <div>
                        <MediaUploader
                          label="LR Copy Document"
                          fileUrl={stageModal.lrCopy}
                          onUploadSuccess={url => setStageModal(p => ({ ...p, lrCopy: url }))}
                          hint="Upload LR Copy"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Email Notification options */}
              <div style={{ background: 'rgba(96,165,250,0.05)', padding: '12px 14px', borderRadius: '8px', border: '1px solid rgba(96,165,250,0.2)', marginBottom: '14px' }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#60a5fa', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <HiOutlineMail /> Customer Email Notification
                </div>
                <input
                  type="email"
                  className="form-input"
                  style={{ fontSize: '0.8rem', marginBottom: '8px' }}
                  value={stageModal.customerEmail}
                  onChange={e => setStageModal(p => ({ ...p, customerEmail: e.target.value }))}
                  placeholder="customer@example.com (Recipient Email)"
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-dim)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={stageModal.notifyCustomerEmail}
                    onChange={e => setStageModal(p => ({ ...p, notifyCustomerEmail: e.target.checked }))}
                  />
                  Automatically send email notification with proof attachments and tracking info
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button type="button" className="btn btn-cancel" onClick={() => setStageModal({ open: false, dispatch: null, targetStage: '' })}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <HiOutlineSave /> {submitting ? 'Saving...' : stageModal.targetStage === 'packing' ? 'Confirm & Move to Packing' : 'Confirm & Mark Dispatched'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* FULL CREATE / EDIT MODAL - MULTI-STEP WIZARD */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '680px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                  {editingId ? 'Edit Order & Attachments' : 'New Order for Assembly & Dispatch'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                  Step {modalStep} of 4: {modalStep === 1 ? '🔧 Assembly & Order Details' : modalStep === 2 ? '📦 Box Packing Details & Proof' : modalStep === 3 ? '🧾 Billing & Invoice Documents' : '🚚 Logistics Dispatch & Outward Pass'}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}><HiOutlineX /></button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
              {/* Interactive Step Navigator */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px',
              padding: '6px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid var(--border)'
            }}>
              {[
                { step: 1, label: '1. Assembly', icon: '🔧', desc: 'Order & Links' },
                { step: 2, label: '2. Packing', icon: '📦', desc: 'Boxes & Wt' },
                { step: 3, label: '3. Billing', icon: '🧾', desc: 'Invoice & Tax' },
                { step: 4, label: '4. Dispatch', icon: '🚚', desc: 'LR & Courier' }
              ].map(s => {
                const isActive = modalStep === s.step;
                const isPassed = modalStep > s.step;
                return (
                  <button
                    key={s.step}
                    type="button"
                    onClick={() => {
                      if (s.step > 1 && !form.customerName?.trim()) {
                        toast.error('Please enter Customer Name first');
                        return;
                      }
                      setModalStep(s.step);
                    }}
                    style={{
                      border: isActive ? '1.5px solid var(--primary)' : '1px solid transparent',
                      background: isActive ? 'rgba(99,102,241,0.15)' : (isPassed ? 'rgba(34,197,94,0.08)' : 'transparent'),
                      color: isActive ? 'var(--primary)' : (isPassed ? '#10b981' : 'var(--text-dim)'),
                      borderRadius: '8px', padding: '8px 4px', cursor: 'pointer', textAlign: 'center',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span>{s.icon}</span> <span>{s.label}</span>
                    </div>
                    <div style={{ fontSize: '0.68rem', opacity: 0.8, marginTop: '2px' }}>{s.desc}</div>
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleSubmit}>
              {/* STEP 1: ASSEMBLY & ORDER DETAILS */}
              {modalStep === 1 && (
                <div>
                  {/* Customer & Order Reference */}
                  <div style={{ marginBottom: '14px' }}>
                    <label className="form-label">Customer Name *</label>
                    <input className="form-input" required value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} placeholder="e.g. Acme Automation" />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label">Contact / Phone</label>
                      <input className="form-input" value={form.customerContact} onChange={e => setForm(p => ({ ...p, customerContact: e.target.value }))} placeholder="+91..." />
                    </div>
                    <div>
                      <label className="form-label">Customer Email Address</label>
                      <input className="form-input" type="email" value={form.customerEmail} onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))} placeholder="customer@example.com" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Sales Order Ref (Prepopulate Items) *</span>
                        {form.salesOrderRef && (
                          <span style={{ fontSize: '0.72rem', color: 'var(--primary)', fontWeight: 600 }}>
                            ✓ {form.salesOrderRef}
                          </span>
                        )}
                      </label>
                      <select
                        className="form-input"
                        style={{ cursor: 'pointer', fontWeight: 600, background: 'var(--bg-input)' }}
                        value={salesOrders.some(so => so.salesOrderRef === form.salesOrderRef) ? form.salesOrderRef : (form.salesOrderRef ? '__custom__' : '')}
                        onChange={e => handleSelectSalesOrder(e.target.value)}
                      >
                        <option value="">-- Select Sales Order to Auto-fill --</option>
                        {salesOrders.map(so => (
                          <option key={so.salesOrderRef} value={so.salesOrderRef}>
                            {so.salesOrderRef} — {so.customerName} ({so.items?.length || 0} items)
                          </option>
                        ))}
                        <option value="__custom__">-- Manual / Custom SO Reference --</option>
                      </select>

                      {(!salesOrders.some(so => so.salesOrderRef === form.salesOrderRef) || form.salesOrderRef === '__custom__') && (
                        <input
                          className="form-input"
                          style={{ marginTop: '8px' }}
                          value={form.salesOrderRef === '__custom__' ? '' : form.salesOrderRef}
                          onChange={e => setForm(p => ({ ...p, salesOrderRef: e.target.value }))}
                          placeholder="Type custom SO Ref (e.g. SO-2026-001)"
                        />
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '22px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'var(--text-dim)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={form.notifyCustomerEmail}
                          onChange={e => setForm(p => ({ ...p, notifyCustomerEmail: e.target.checked }))}
                        />
                        Send Email Notification on Save
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label className="form-label">Delivery Address</label>
                    <textarea className="form-input" rows={2} value={form.customerAddress} onChange={e => setForm(p => ({ ...p, customerAddress: e.target.value }))} placeholder="Full shipping address..." />
                  </div>

                  {/* Items */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label className="form-label" style={{ margin: 0 }}>Dispatch Items</label>
                      <button type="button" onClick={addItem} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--primary)', cursor: 'pointer', padding: '3px 10px', fontSize: '0.8rem' }}>+ Add Item</button>
                    </div>
                    {form.items.map((item, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 80px auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={item.modelNumber} onChange={e => updateItem(idx, 'modelNumber', e.target.value)} placeholder="Model No." />
                        <input className="form-input" style={{ fontSize: '0.8rem' }} value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Description" />
                        <input className="form-input" style={{ fontSize: '0.8rem' }} type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, 'quantity', Number(e.target.value))} />
                        <select className="form-input" style={{ fontSize: '0.8rem' }} value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)}>
                          <option value="meters">meters</option>
                          <option value="pcs">pcs</option>
                          <option value="kg">kg</option>
                          <option value="boxes">boxes</option>
                        </select>
                        {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} style={{ background: 'rgba(239,68,68,0.1)', border: 'none', color: '#ef4444', cursor: 'pointer', borderRadius: '4px', padding: '4px 6px' }}><HiOutlineX /></button>}
                      </div>
                    ))}
                  </div>

                  {/* Status */}
                  <div style={{ marginBottom: '16px' }}>
                    <label className="form-label">Workflow Stage</label>
                    <select className="form-input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                    </select>
                  </div>

                  {/* Section 1: Assembly Stage Proofs */}
                  <div style={{ background: 'rgba(245,158,11,0.03)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.2)', padding: '14px', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#f59e0b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🔧 Step 1: Assembly Details & Photo/Video Proof
                    </div>
                    <MediaUploader
                      label="Assembly Photo / Video"
                      fileUrl={form.assemblyMediaUrl}
                      onUploadSuccess={url => setForm(p => ({ ...p, assemblyMediaUrl: url }))}
                      hint="Upload assembly photo or video"
                    />
                    <div style={{ marginTop: '8px' }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Assembly Notes</label>
                      <input className="form-input" style={{ fontSize: '0.8rem' }} value={form.assemblyNotes} onChange={e => setForm(p => ({ ...p, assemblyNotes: e.target.value }))} placeholder="e.g. Assembled 20m drag chain with brackets" />
                    </div>
                  </div>

                  {/* Step 1 Footer Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <button type="button" className="btn btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={submitting}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                      >
                        💾 Save as Draft
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          if (!form.customerName?.trim()) {
                            toast.error('Please enter Customer Name to proceed');
                            return;
                          }
                          setModalStep(2);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        Next: 📦 Step 2 (Packing Details) ➔
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: PACKING DETAILS & PROOF */}
              {modalStep === 2 && (
                <div>
                  <div style={{ background: 'rgba(59,130,246,0.03)', borderRadius: '10px', border: '1px solid rgba(59,130,246,0.2)', padding: '16px', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#3b82f6', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      📦 Step 2: Packing Details & Photo/Video Proof
                    </div>
                    <MediaUploader
                      label="Packing Photo / Video"
                      fileUrl={form.packingMediaUrl}
                      onUploadSuccess={url => setForm(p => ({ ...p, packingMediaUrl: url }))}
                      hint="Upload photo or video of packed cartons"
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Box Count</label>
                        <input className="form-input" type="number" min={1} style={{ fontSize: '0.85rem' }} value={form.totalBoxes} onChange={e => setForm(p => ({ ...p, totalBoxes: e.target.value }))} placeholder="e.g. 3 Boxes" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Weight</label>
                        <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.totalWeight} onChange={e => setForm(p => ({ ...p, totalWeight: e.target.value }))} placeholder="e.g. 45 KG" />
                      </div>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Packing Notes</label>
                      <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.packingNotes} onChange={e => setForm(p => ({ ...p, packingNotes: e.target.value }))} placeholder="e.g. Bubble-wrapped, strapped with fragile label" />
                    </div>
                  </div>

                  {/* Step 2 Footer Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setModalStep(1)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      ⬅ Back to Step 1 (Assembly)
                    </button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={submitting}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                      >
                        💾 Save as Draft
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => setModalStep(3)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Next: 🧾 Step 3 (Billing & Invoices) ➔
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: BILLING & INVOICE DOCUMENTS */}
              {modalStep === 3 && (
                <div>
                  <div style={{ background: 'rgba(16,185,129,0.03)', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.2)', padding: '16px', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#10b981', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🧾 Step 3: Billing & Invoice Documents
                    </div>
                    <MediaUploader
                      label="Tax Invoice / Bill Document (PDF / Image)"
                      fileUrl={form.billingInvoiceUrl}
                      onUploadSuccess={url => setForm(p => ({ ...p, billingInvoiceUrl: url }))}
                      hint="Upload Bill / Invoice PDF or Image"
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Invoice Number</label>
                        <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.invoiceNumber} onChange={e => setForm(p => ({ ...p, invoiceNumber: e.target.value }))} placeholder="e.g. INV-2026-004" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Invoice Total Amount (₹)</label>
                        <input className="form-input" type="number" style={{ fontSize: '0.85rem' }} value={form.invoiceAmount} onChange={e => setForm(p => ({ ...p, invoiceAmount: e.target.value }))} placeholder="e.g. 54000" />
                      </div>
                    </div>
                  </div>

                  {/* Step 3 Footer Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setModalStep(2)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      ⬅ Back to Step 2 (Packing)
                    </button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={submitting}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                      >
                        💾 Save as Draft
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => setModalStep(4)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Next: 🚚 Step 4 (Dispatch & Logistics) ➔
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: DISPATCH & LOGISTICS DETAILS */}
              {modalStep === 4 && (
                <div>
                  <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '12px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🚚 Step 4: Dispatch Logistics, LR & POD
                    </div>
                    <MediaUploader
                      label="Dispatch / Vehicle Loading Photo / Video"
                      fileUrl={form.dispatchMediaUrl}
                      onUploadSuccess={url => setForm(p => ({ ...p, dispatchMediaUrl: url }))}
                      hint="Upload vehicle loading or final dispatch proof"
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Courier / Transporter Name</label>
                        <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.courier} onChange={e => setForm(p => ({ ...p, courier: e.target.value }))} placeholder="e.g. BlueDart / DTDC / V-Trans" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>Tracking / Consignment #</label>
                        <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.trackingNumber} onChange={e => setForm(p => ({ ...p, trackingNumber: e.target.value }))} placeholder="e.g. 12345678" />
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.8rem' }}>LR Number</label>
                        <input className="form-input" style={{ fontSize: '0.85rem' }} value={form.lrNumber} onChange={e => setForm(p => ({ ...p, lrNumber: e.target.value }))} placeholder="Lorry Receipt No." />
                      </div>
                      <div>
                        <MediaUploader
                          label="LR Copy File"
                          fileUrl={form.lrCopyUrl}
                          onUploadSuccess={url => setForm(p => ({ ...p, lrCopyUrl: url }))}
                          hint="Upload LR copy PDF/Image"
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <MediaUploader
                        label="Proof of Delivery (POD) Receipt"
                        fileUrl={form.podUrl}
                        onUploadSuccess={url => setForm(p => ({ ...p, podUrl: url }))}
                        hint="Upload signed POD receipt"
                      />
                    </div>
                    {form.status === 'hold' && (
                      <div style={{ marginTop: '12px' }}>
                        <label className="form-label" style={{ fontSize: '0.8rem', color: '#ef4444' }}>Hold Reason *</label>
                        <input className="form-input" value={form.holdReason} onChange={e => setForm(p => ({ ...p, holdReason: e.target.value }))} placeholder="Reason for placing on hold..." />
                      </div>
                    )}
                  </div>

                  {/* Step 4 Footer Buttons (Submit) */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setModalStep(3)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      ⬅ Back to Step 3 (Billing)
                    </button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={submitting}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                      >
                        💾 Save as Draft
                      </button>
                      <button type="button" className="btn btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' }}>
                        <HiOutlineSave /> {submitting ? 'Saving...' : editingId ? 'Update & Save Order' : '💾 Save & Complete Order'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
            </div>
          </div>
        </div>
      )}

      {/* MEDIA PREVIEW MODAL (LIGHTBOX / VIDEO PLAYER / PDF) */}
      {previewMedia && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px'
        }} onClick={() => setPreviewMedia(null)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '14px', maxWidth: '800px', width: '100%',
            overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {previewMedia.title || 'Attachment Preview'}
              </h4>
              <button onClick={() => setPreviewMedia(null)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}>
                <HiOutlineX />
              </button>
            </div>

            <div style={{ padding: '20px', textAlign: 'center', maxHeight: '75vh', overflowY: 'auto' }}>
              {previewMedia.type === 'video' ? (
                <video src={formatExternalUrl(previewMedia.url)} controls autoPlay style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: '8px' }} />
              ) : isPdfUrl(previewMedia.url) ? (
                <div>
                  <iframe src={formatExternalUrl(previewMedia.url)} title="PDF Preview" style={{ width: '100%', height: '60vh', border: 'none', borderRadius: '8px' }} />
                </div>
              ) : (
                <img src={formatExternalUrl(previewMedia.url)} alt="Proof Preview" style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '8px' }} />
              )}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href={formatExternalUrl(previewMedia.url)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                🔗 Open Original in New Tab
              </a>
              <button onClick={() => setPreviewMedia(null)} className="btn btn-primary btn-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Dispatch Quality Check (shared component, meters only) */}
      {showQcModal && qcOrder && (
        <DispatchQcModal
          order={qcOrder}
          onClose={handleCloseQcModal}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}
